import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { useAuth } from './contexts/AuthContext.jsx';
import AdminLayout from './components/AdminLayout.jsx';
import AcceptInvitePage from './pages/AcceptInvitePage.jsx';
import HomePage from './pages/HomePage.jsx';
import InvitesAdminPage from './pages/admin/InvitesAdminPage.jsx';
import LoginPage from './pages/LoginPage.jsx';
import MfaChallengePage from './pages/MfaChallengePage.jsx';
import MfaRequiredPage from './pages/MfaRequiredPage.jsx';
import TracksPage from './pages/TracksPage.jsx';
import TrackDetailPage from './pages/TrackDetailPage.jsx';
import ArchivePage from './pages/ArchivePage.jsx';
import TracksAdminPage from './pages/admin/TracksAdminPage.jsx';
import RequestInvitePage from './pages/RequestInvitePage.jsx';
import InviteRequestsAdminPage from './pages/admin/InviteRequestsAdminPage.jsx';
import UsersAdminPage from './pages/admin/UsersAdminPage.jsx';
import MobileNav from './components/MobileNav.jsx';
import ProfilePage from './pages/ProfilePage.jsx';
import QuotesPage from './pages/QuotesPage.jsx';
import QuotesAdminPage from './pages/admin/QuotesAdminPage.jsx';
import OAuthAuthorizePage from './pages/OAuthAuthorizePage.jsx';
import FunnyPage from './pages/FunnyPage.jsx';
import LaunchesPage from './pages/LaunchesPage.jsx';
import ComicDetailPage from './pages/ComicDetailPage.jsx';
import AdminComicsPage from './pages/admin/AdminComicsPage.jsx';


/**
 * Protects routes that require admin role.
 * Non-admins (but signed in) get bounced home rather than to login.
 */
function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <LoadingFlash />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

/**
 * If the user is already signed in, skip this route (login page).
 */
function RedirectIfAuthed({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function LoadingFlash() {
  return (
    <div style={{
      padding: 40,
      fontFamily: 'ui-monospace, monospace',
      color: '#8a8373',
    }}>…</div>
  );
}


export default function App() {
  return (
    <>
      <MobileNav />
      <Routes>
        {/* ── public ── */}
        <Route path="/" element={<HomePage />} />

        <Route
          path="/login"
          element={
            <RedirectIfAuthed>
              <LoginPage />
            </RedirectIfAuthed>
          }
        />
        <Route path="/mfa-challenge" element={<MfaChallengePage />} />
        <Route path="/mfa-required" element={<MfaRequiredPage />} />
        <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
        <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
        <Route path="/request-invite" element={<RequestInvitePage />} />
        <Route path="/tracks" element={<TracksPage />} />
        <Route path="/tracks/archive" element={<ArchivePage />} />
        <Route path="/tracks/:id" element={<TrackDetailPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/quotes" element={<QuotesPage />} />
        <Route path="/funny" element={<FunnyPage />} />
        <Route path="/funny/:id" element={<ComicDetailPage />} />
        <Route path="/launches" element={<LaunchesPage />} />

        {/* ── admin ── */}
        <Route
          path="/admin"
          element={
            <RequireAdmin>
              <AdminLayout />
            </RequireAdmin>
          }
        >
          <Route index element={<Navigate to="/admin/invites" replace />} />
          <Route path="tracks" element={<TracksAdminPage />} />
          <Route path="invites" element={<InvitesAdminPage />} />
          <Route path="requests" element={<InviteRequestsAdminPage />} />
          <Route path="users" element={<UsersAdminPage />} />
          <Route path="quotes" element={<QuotesAdminPage />} />
          <Route path="comics" element={<AdminComicsPage />} />
        </Route>

        {/* ── catch-all ── */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
